/**
 * DotRating — 1-5 dot scale (UX §3.6). NEVER stars.
 * Read-only and editable variants. Interactive variant is a radiogroup
 * with arrow-key cycling + Space to select per AC-104.
 *
 * Sizes: sm 8px / md 10px / lg 14px.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export type DotSize = 'sm' | 'md' | 'lg';
export type DotValue = 0 | 1 | 2 | 3 | 4 | 5;

export interface DotRatingProps {
  value: DotValue;
  onChange?: (next: 1 | 2 | 3 | 4 | 5) => void;
  size?: DotSize;
  readOnly?: boolean;
  disabled?: boolean;
  /** Accessible label for the whole group (defaults to "Rate 1 to 5 dots"). */
  label?: string;
  className?: string;
}

const SIZE_PX: Record<DotSize, number> = { sm: 8, md: 10, lg: 14 };
const SIZE_GAP: Record<DotSize, string> = {
  sm: 'gap-1',
  md: 'gap-1.5',
  lg: 'gap-2',
};

export function DotRating({
  value,
  onChange,
  size = 'md',
  readOnly = false,
  disabled = false,
  label = 'Rate 1 to 5 dots',
  className,
}: DotRatingProps) {
  const interactive = !readOnly && !disabled && Boolean(onChange);
  const [hovered, setHovered] = React.useState<number | null>(null);
  const dotPx = SIZE_PX[size];

  function commit(next: number) {
    if (!interactive) return;
    const clamped = Math.max(1, Math.min(5, next)) as 1 | 2 | 3 | 4 | 5;
    onChange?.(clamped);
  }

  function onKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    if (!interactive) return;
    if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') {
      ev.preventDefault();
      commit(Math.max(1, value - 1));
    } else if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      commit(Math.min(5, value + 1));
    } else if (ev.key === 'Home') {
      ev.preventDefault();
      commit(1);
    } else if (ev.key === 'End') {
      ev.preventDefault();
      commit(5);
    } else if (/^[1-5]$/.test(ev.key)) {
      ev.preventDefault();
      commit(Number(ev.key));
    }
  }

  return (
    <div
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={interactive ? label : `Rated ${value} of 5 dots`}
      tabIndex={interactive ? 0 : -1}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHovered(null)}
      className={cn(
        'inline-flex items-center',
        SIZE_GAP[size],
        disabled && 'opacity-40',
        interactive &&
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
        className,
      )}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const isFilled = i <= value;
        const isHoverPreview =
          interactive && hovered !== null && i <= hovered && !isFilled;
        if (interactive) {
          return (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={i === value}
              aria-label={`${i} of 5`}
              onMouseEnter={() => setHovered(i)}
              onClick={() => commit(i)}
              tabIndex={-1}
              className={cn(
                'rounded-full border transition-colors duration-200 ease-out-slow',
                isFilled
                  ? 'bg-accent border-accent'
                  : isHoverPreview
                    ? 'bg-accent/30 border-accent/30'
                    : 'bg-transparent border-ink-300',
              )}
              style={{ width: dotPx, height: dotPx }}
            />
          );
        }
        return (
          <span
            key={i}
            aria-hidden
            className={cn(
              'rounded-full border',
              isFilled
                ? 'bg-accent border-accent'
                : 'bg-transparent border-ink-300',
            )}
            style={{ width: dotPx, height: dotPx }}
          />
        );
      })}
    </div>
  );
}
